import React, { Fragment, ReactNode, useState } from 'react';
import { ArchiveSlotInvoiceActionEnum } from '../../../molecules/ActionSelect';
import { ICanUseOtherPaymentMethod, IPayingUser, IPaymentMethod } from '../../../molecules/PaymentMethodPicker';
import { PaymentMethodStep } from './PaymentMethodStep';
import { SummaryStep } from './SummaryStep';

interface RefundStepsProps extends ICanUseOtherPaymentMethod {
	step?: number;
	amountToRefund: number;
	usedPaymentMethods: IPaymentMethod[];
	lineItemCount?: number;
	refundResponse?: IPaymentMethod[];
	goToResourceType?: 'program' | 'membership';
	handleSelectedMethods: (val: IPaymentMethod[]) => void;
	initialMail?: string;
	handleSelectAction: (val: ArchiveSlotInvoiceActionEnum) => void;
	sendReceiptAction: (type: 'email' | 'sms', toAddress: string) => void;
	handleDisableButton?: (val: boolean) => void;
	goToResource?: () => void;
	disableActions?: boolean;
	isReservationRefund?: boolean;
	/** When true, only "Refund and Leave Balance Due" is shown (leave-balance submit). */
	isFeesOnlyRefund?: boolean;
	isPublicInvoice?: boolean;
	invoiceOwner?: IPayingUser;
	replenishStockOption?: ReactNode;
	onHideReplenishStock?: () => void;
}

export const RefundSteps = ({
	step,
	amountToRefund,
	usedPaymentMethods,
	lineItemCount,
	refundResponse,
	goToResourceType,
	handleSelectedMethods,
	handleDisableButton,
	initialMail,
	handleSelectAction,
	sendReceiptAction,
	goToResource,
	disableActions = false,
	isReservationRefund = false,
	isFeesOnlyRefund = false,
	isPublicInvoice = false,
	invoiceOwner,
	canUseOtherPaymentMethod,
	replenishStockOption,
	onHideReplenishStock,
}: RefundStepsProps) => {
	const [action, setAction] = useState<ArchiveSlotInvoiceActionEnum>();
	const handleActionSelect = (val: ArchiveSlotInvoiceActionEnum) => {
		setAction(val);
		handleSelectAction(val);
		if (val !== ArchiveSlotInvoiceActionEnum.REFUND_AND_VOID) {
			onHideReplenishStock?.();
		}
	};

	/** Stock is only restored when the item is voided, so the option is tied to refund-and-void. */
	const showReplenishStockOption = Boolean(
		replenishStockOption &&
			!isReservationRefund &&
			!isFeesOnlyRefund &&
			!disableActions &&
			action === ArchiveSlotInvoiceActionEnum.REFUND_AND_VOID
	);

	switch (step) {
		case 0:
			return (
				<PaymentMethodStep
					amountToRefund={amountToRefund}
					usedPaymentMethods={usedPaymentMethods}
					handleSelectAction={handleActionSelect}
					handleSelectedMethods={handleSelectedMethods}
					disableActions={disableActions}
					isReservationRefund={isReservationRefund}
					isFeesOnlyRefund={isFeesOnlyRefund}
					invoiceOwner={invoiceOwner}
					handleDisable={handleDisableButton}
					isPublicInvoice={isPublicInvoice}
					canUseOtherPaymentMethod={canUseOtherPaymentMethod}
					replenishStockOption={replenishStockOption}
					showReplenishStockOption={showReplenishStockOption}
				/>
			);
		case 1:
			return (
				<SummaryStep
					lineItemCount={lineItemCount}
					refundResponse={refundResponse}
					onChangeSendReceipt={sendReceiptAction}
					goToResource={goToResource}
					initialMail={initialMail}
					action={action}
					goToResourceType={goToResourceType}
					paymentMethods={usedPaymentMethods}
					invoiceOwner={invoiceOwner}
				/>
			);
		default:
			return <Fragment />;
	}
};
